import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useContext, useState } from "react";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type TagTone,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, isDefined } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  type PageSearch,
  pageQuery,
  validatePageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";
import { taskPlanName } from "@bfc-software/bfc-web-ui/lib/task-utils/naming";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";

import { type TaskPlanError } from "./-components/types";
import styles from "./index.module.css";

/**
 * `planId` narrows the listing to one task plan. It lives in the URL rather
 * than component state so the task plans page can link straight to a filtered
 * listing, and so a filtered view stays bookmarkable. One id here, but the API
 * takes `planIds` as an array -- the URL carries the single case because that
 * is the only entry point that exists.
 */
type TaskPlanErrorSearch = { planId?: string } & PageSearch;

/**
 * Rows per request.
 *
 * Held here rather than shared, because the server derives `page_number` and
 * `page_count` from the limit of the request that produced them -- so the limit
 * is part of what a page ordinal means, and a route that changes it renumbers
 * its own pages and nobody else's. Not user-adjustable: page size in the URL
 * would renumber pages under the reader mid-walk.
 */
const PAGE_SIZE = 25;

/** Header cells below, and both colSpan rows, have to agree with this. */
const COLUMN_COUNT = 5;

/**
 * `plan_status` shares the task plan status vocabulary, so it shares the tones.
 * Local copy rather than an import: the task plans page keys its map on
 * `TaskPlan["status"]` and does not export it.
 */
const PLAN_STATUS_TONES: Record<TaskPlanError["plan_status"], TagTone> = {
  completed: "success",
  processing: "info",
  queued: "neutral",
  failed: "danger",
};

/** `severity` is the error's own axis: a warning is not a failed plan. */
const SEVERITY_TONES: Record<TaskPlanError["severity"], TagTone> = {
  error: "danger",
  warning: "warning",
};

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plan-errors/",
)({
  validateSearch: (search?: Record<string, unknown>): TaskPlanErrorSearch => {
    const raw = search?.["planId"];
    return {
      ...(typeof raw === "string" && raw.length > 0 ? { planId: raw } : {}),
      ...validatePageSearch(search),
    };
  },
  staticData: {
    crumb: (_: unknown, t: TFunction) => ({
      label: t("page.taskPlanErrors.crumb"),
    }),
  },
  component: RouteComponent,
});

function ErrorRow({
  error,
  isExpanded,
  onToggle,
  t,
}: {
  error: TaskPlanError;
  isExpanded: boolean;
  onToggle: () => void;
  t: TFunction;
}) {
  const { lang, org, location } = Route.useParams();

  return (
    <>
      <TableRow data-test-role="task-plan-error-row">
        {/* Link text is the friendly name; the raw ULID moves to the expansion row. */}
        <TableCell>
          <Link
            to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan"
            params={{ "task-plan": error.task_plan_id, org, location, lang }}
            title={t("page.taskPlanErrors.viewPlanHint")}
            data-test-role="task-plan-error-plan"
          >
            {taskPlanName(error.task_plan_internal_id)}
          </Link>
        </TableCell>

        <TableCell>{error.error_code}</TableCell>

        {/*
          The message cell clamps to one line and hands the full text to the
          expansion row. Pallet's Button takes no `title`, so the hover text
          rides the preview span.
        */}
        <TableCell className={styles.messageCell}>
          <span className={styles.messagePreview} title={error.message}>
            {error.message}
          </span>
          <Button
            kind="text-only"
            variant="secondary"
            scale="inline"
            text={
              isExpanded
                ? t("page.taskPlanErrors.collapse")
                : t("page.taskPlanErrors.expand")
            }
            onPress={onToggle}
            data-test-role="task-plan-error-toggle"
          />
        </TableCell>

        {/* The owning plan's routes, echoed onto the error row. */}
        <TableCell>
          <div className={styles.routeList}>
            {error.routes.map((route) => (
              <span
                key={`${error.id}-${route.route_code}`}
                className={styles.routeItem}
                data-test-role="task-plan-error-route-code"
              >
                {route.route_code}
              </span>
            ))}
          </div>
        </TableCell>

        <TableCell>
          <DateValue dateValue={error.created_at} />
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow data-test-role="task-plan-error-expanded">
          <TableCell colSpan={COLUMN_COUNT}>
            <dl className={styles.detailGrid}>
              <DetailItem label={t("page.taskPlanErrors.table.severity")}>
                <span data-test-role="task-plan-error-severity">
                  <Tag
                    label={t(`page.taskPlanErrors.severity.${error.severity}`)}
                    display={{ tone: SEVERITY_TONES[error.severity] }}
                  />
                </span>
              </DetailItem>
              <DetailItem label={t("page.taskPlanErrors.table.planStatus")}>
                <span data-test-role="task-plan-error-plan-status">
                  <Tag
                    label={error.plan_status}
                    display={{ tone: PLAN_STATUS_TONES[error.plan_status] }}
                  />
                </span>
              </DetailItem>
              <DetailItem label={t("page.taskPlanErrors.table.fieldPath")}>
                {isDefined(error.field_path) ? (
                  error.field_path
                ) : (
                  <span className={styles.emptyValue}>—</span>
                )}
              </DetailItem>
              <DetailItem label={t("page.taskPlanErrors.table.line")}>
                {isDefined(error.line_number) ? (
                  error.line_number
                ) : (
                  <span className={styles.emptyValue}>—</span>
                )}
              </DetailItem>
              <DetailItem label={t("page.taskPlanErrors.table.error")}>
                <span className={styles.truncatedId} title={error.id}>
                  {error.id}
                </span>
              </DetailItem>
            </dl>
            <div className={styles.expandedMessage}>{error.message}</div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.detailItem}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { planId, ...page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useTaskPlannerApi();

  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const tenantId = AuthUtils.getTenantId(authState, orgId, location);

  const enabled = typeof orgId === "string";

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // A new Set per toggle, so React sees a new reference. The task detail pick
  // table mutates the Set already in state and only re-renders because its
  // wrapper object is spread; that is not a pattern to carry forward.
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);

      if (!next.delete(id)) {
        next.add(id);
      }

      return next;
    });

  const query = client.useQuery(
    "get",
    "/{orgId}/task-plan-errors",
    {
      params: {
        path: { orgId: orgId! },
        query: {
          limit: PAGE_SIZE,
          warehouseExtId: location,
          ...(isDefined(planId) ? { planIds: [planId] } : {}),
          ...pageQuery(page),
        },
      },
    },
    { enabled },
  );

  const errors = query.data?.task_plan_errors ?? [];

  return (
    <div
      className="bg-surface h-full w-full"
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      <div className="action-bar">
        <div className="action-bar-left">
          <div className={styles.pageHeading}>
            <h1>{t("page.taskPlanErrors.title")}</h1>
            {/*
              The filter is only visible in the URL, so an arrival from the
              task plans page would otherwise look like a warehouse with
              exactly one erroring plan in it.
            */}
            {isDefined(planId) ? (
              <span
                className={styles.filterNotice}
                data-test-role="plan-filter"
              >
                {t("page.taskPlanErrors.filteredTo", { planId })}
                <Link
                  to="/$lang/orgs/$org/internal/whs/$location/task-plan-errors"
                  params={{ lang, org, location }}
                  search={{}}
                  data-test-role="plan-filter-clear"
                >
                  {t("page.taskPlanErrors.clearFilter")}
                </Link>
              </span>
            ) : null}
          </div>

          <ListPager
            pagination={query.data?.pagination}
            isFetching={query.isFetching}
            onChange={(next) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  cursor: next.cursor,
                  dir: next.dir,
                }),
              })
            }
            t={t}
          />
        </div>
        <div className="action-bar-right">
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("page.taskPlanErrors.refresh")}
            isDisabled={query.isFetching}
            onPress={() => query.refetch()}
            data-test-role="task-plan-errors-refresh"
          />
        </div>
      </div>
      {query.isLoading || !enabled ? (
        <Loader type="page" />
      ) : query.isError ? (
        <AppError
          description={t("page.taskPlanErrors.failed")}
          resolution={t("page.taskPlanErrors.failureSupport")}
        />
      ) : (
        <Table
          aria-label={t("page.taskPlanErrors.table.ariaLabel")}
          className={errors.length < 1 ? "min-h-full" : ""}
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("page.taskPlanErrors.table.plan")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlanErrors.table.code")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlanErrors.table.message")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlanErrors.table.routes")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlanErrors.table.created")}
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {errors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <EmptyState heading={t("page.taskPlanErrors.empty")} />
                </TableCell>
              </TableRow>
            ) : (
              errors.map((error) => (
                <ErrorRow
                  key={error.id}
                  error={error}
                  isExpanded={expanded.has(error.id)}
                  onToggle={() => toggle(error.id)}
                  t={t}
                />
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
